// Libraries
import { Center } from '@chakra-ui/react';
// Components
import { Spinner } from '@atoms/index';
import { EmissionsImpact, EmissionsLayout, EmptyResults } from './components';
// Types
import { SimulationsResultsCardProps } from './SimulationsResultsCard.types';

/**
 * `SimulationsResultsCard` is a component that presents the results of emissions simulations.
 * Depending on the status of the simulation, it may show loading, the simulation results, or a message indicating that there are no results.
 *
 * @component
 * @param {SimulationsResultsCardProps} props - The properties that define the `SimulationsResultsCard` component.
 * @param {boolean} props.isLoading - A flag indicating whether the simulation is currently in progress.
 * @param {boolean} [props.wasUpdated=true] - A flag indicating whether the simulation data was updated.
 * @param {Object} props.emissionsImpact - The results data of the emissions simulation.
 *
 * @returns {ReactElement} Returns a card component that may contain a loading spinner, emissions impact data, or a message indicating no results.
 */
export const SimulationsResultsCard = ({
  isLoading = false,
  wasUpdated = true,
  emissionsImpact,
  bulkFuelIsGreater = false,
  bulkQuantityUsedMT,
}: SimulationsResultsCardProps) => {
  if (isLoading)
    return (
      <EmissionsLayout>
        <Center w='full' minH={{ base: 57, md: 62.5 }}>
          <Spinner size='xl' />
        </Center>
      </EmissionsLayout>
    );

  if (wasUpdated && emissionsImpact) {
    return (
      <EmissionsLayout>
        <EmissionsImpact
          data={emissionsImpact}
          bulkFuelIsGreater={bulkFuelIsGreater}
          bulkQuantityUsedMT={bulkQuantityUsedMT}
        />
      </EmissionsLayout>
    );
  }

  return (
    <EmissionsLayout>
      <EmptyResults />
    </EmissionsLayout>
  );
};
